import type { FieldValues, UseFormReturn } from "react-hook-form";
import { FormProvider } from "react-hook-form";

import {
  FormBody,
  FormContainer,
  FormFooter,
  FormRoot as DesignSystemFormRoot,
  type FormRootProps,
  FormSplitContent,
} from "@/design-system/Form";
import { usePrompt } from "@/hooks/usePrompt";
import { useTranslation } from "@/translations";

type DefaultContext = object;

interface Props<TFormValues extends FieldValues, TContext extends DefaultContext> extends Pick<
  FormRootProps,
  "align" | "fullWidth" | "gap" | "maxHeight"
> {
  formMethods: UseFormReturn<TFormValues, TContext>;
  onSubmit: (values: TFormValues) => unknown;
  noPrompt?: boolean;
  stopSubmitPropagation?: boolean;
  id?: string;
  "data-testid"?: string;
}

function FormRoot<TFormValues extends FieldValues, TContext extends DefaultContext = DefaultContext>({
  align,
  fullWidth,
  gap,
  maxHeight,
  formMethods,
  onSubmit,
  children,
  noPrompt,
  stopSubmitPropagation,
  id,
  ...props
}: React.PropsWithChildren<Props<TFormValues, TContext>>): React.ReactNode {
  const { t } = useTranslation();

  usePrompt(
    t("component.prompt.confirmation"),
    !noPrompt && Object.keys(formMethods.formState.dirtyFields).length > 0 && !formMethods.formState.isSubmitted,
  );

  return (
    <FormProvider {...formMethods}>
      <DesignSystemFormRoot
        align={align}
        fullWidth={fullWidth}
        gap={gap}
        maxHeight={maxHeight}
        id={id}
        onSubmit={(event) => {
          if (stopSubmitPropagation) {
            event.stopPropagation();
          }

          return formMethods.handleSubmit((data) => onSubmit(data))(event);
        }}
        data-testid={props["data-testid"]}
        noValidate
      >
        {children}
      </DesignSystemFormRoot>
    </FormProvider>
  );
}

export const Form = {
  Root: FormRoot,
  Container: FormContainer,
  Body: FormBody,
  Footer: FormFooter,
  SplitContent: FormSplitContent,
};
